/**
 * Participation sheet rows for ONE offer participant — Josh's own layout (10/28 screenshot of what he sends today):
 *
 *   Funded                              $25,000.00
 *   Syndicate Syndication Percentage    50.0000%
 *   Syndicate Funded Amount             $12,500.00
 *   Fees to Distribute to Syndicate     $625.00
 *   Broker Comm to Collect              $0.00
 *   -
 *   Total Owed to PFS                   $12,500.00   (red)
 *   Fees to be added to your Ledger     $625.00      (green)
 *
 * Pure: takes the offer computation (`computation.funding`) and one `computation.participants[]` row from
 * GET /syndication-offers/deal/{id} — the same numbers as the wire-in column, the email TERMS block and the position.
 * Nothing is recomputed here; a missing figure renders as a dash, never 0.
 */

const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));

/**
 * "Net potential gain" (Josh 10/28): the estimated return on the % the syndicator goes in on, ASSUMING the deal pays
 * in full — RTR share after the servicing fee PFS keeps, plus the fees credited to the ledger, minus what they wire.
 *   gain = rtr × (1 − servicing%) + fee_to_ledger − wire_in ;  roi = gain ÷ wire_in
 * Returns null when a needed figure is missing (never 0).
 */
export function netPotentialGain(participant, terms) {
  const p = participant || {};
  const rtr = num(p.rtr_target);
  const wire = num(p.wire_in);
  if (rtr === null || wire === null || wire <= 0) return null;
  const servicing = num((terms || {}).servicing_fee_rate) || 0;
  const collected = rtr * (1 - servicing / 100);
  const ledger = num(p.fee_to_ledger) || 0;
  const gain = Math.round((collected + ledger - wire) * 100) / 100;
  return { gain, roi_pct: Math.round((gain / wire) * 10000) / 100, servicing_pct: servicing, collected: Math.round(collected * 100) / 100 };
}

export function participationSheetRows(computation, participant, terms) {
  const p = participant || {};
  const c = computation || {};
  const role = (p.role || '').toUpperCase();
  const rows = [
    { key: 'funded', label: 'Funded', kind: 'money', value: num(c.funding) },
    { key: 'pct', label: 'Syndicate Syndication Percentage', kind: 'pct', value: num(p.final_pct) },
    { key: 'capital', label: 'Syndicate Funded Amount', kind: 'money', value: num(p.capital_share) },
    { key: 'fees', label: 'Fees to Distribute to Syndicate', kind: 'money', value: num(p.origination_fee) },
    { key: 'commission', label: 'Broker Comm to Collect', kind: 'money', value: num(p.commission_share) },
  ];
  if (num(p.iso_charge)) {
    rows.push({ key: 'mgmt', label: `Management Fee (${num(p.management_fee_pct) ?? 0}%)`, kind: 'money', value: num(p.iso_charge) });
  }
  rows.push({ key: 'sep', label: '', kind: 'separator', value: null });
  rows.push({ key: 'owed', label: 'Total Owed to PFS', kind: 'money', value: num(p.wire_in), tone: 'owed' });
  if (role === 'PARTNER') {
    rows.push({ key: 'deducted', label: 'Fees deducted from the wire', kind: 'money', value: num(p.fee_deducted), tone: 'ledger' });
  } else {
    rows.push({ key: 'ledger', label: 'Fees to be added to your Ledger', kind: 'money', value: num(p.fee_to_ledger), tone: 'ledger' });
  }
  rows.push({ key: 'rtr', label: 'Share of the payback (RTR)', kind: 'money', value: num(p.rtr_target) });
  const g = netPotentialGain(p, terms);
  if (g && g.servicing_pct) {
    rows.push({ key: 'after_servicing', label: `RTR after ${g.servicing_pct}% servicing fee`, kind: 'money', value: g.collected });
  }
  rows.push({ key: 'gain', label: 'Net Potential Gain (if paid in full)', kind: 'money', value: g ? g.gain : null, tone: 'gain',
              note: g ? `${g.roi_pct.toFixed(2)}% on $${(num(p.wire_in) || 0).toFixed(2)} in` : null });
  return rows;
}

export function formatSheetValue(row, formatCurrency) {
  if (row.kind === 'separator') return '-';
  if (row.value === null) return '—';
  if (row.kind === 'pct') return `${row.value.toFixed(4)}%`;
  return formatCurrency(row.value);
}
